import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import userService from '../../services/userService';
import TShirtSizeDialog from './TShirtSizeDialog';

const TSHIRT_SIZE_KEY = 'tshirt_size_provided';

/**
 * Check if user has t-shirt size from various sources
 */
const checkHasTShirtSize = (user) => {
  // Check from user object (context)
  if (user?.profile?.tShirtSize) return true;
  if (user?.tShirtSize) return true;

  // Check from localStorage user
  try {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (storedUser?.profile?.tShirtSize) return true;
    if (storedUser?.tShirtSize) return true;
  } catch (e) {
    // Ignore parse errors
  }

  // Check the dedicated flag (set after successful submission)
  if (localStorage.getItem(TSHIRT_SIZE_KEY) === 'true') return true;

  return false;
};

/**
 * TShirtSizePrompt Component
 * Wrapper component that shows the t-shirt size dialog if user hasn't provided their size yet.
 * This component should be placed inside the AuthProvider but wrapping the main app content.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {React.ReactElement} Component with t-shirt size prompt functionality
 */
const TShirtSizePrompt = ({ children }) => {
  const { refreshUserData } = useContext(AuthContext);
  // T-shirt size popup is currently disabled
  // Set ENABLE_TSHIRT_POPUP to true to re-enable
  const ENABLE_TSHIRT_POPUP = false;

  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // T-shirt size popup is disabled - no need to check
  // To re-enable, set ENABLE_TSHIRT_POPUP to true above
  useEffect(() => {
    if (!ENABLE_TSHIRT_POPUP) return;
    // Original logic would go here when re-enabled
  }, [ENABLE_TSHIRT_POPUP]);

  const handleSubmit = useCallback(async (size) => {
    try {
      setLoading(true);
      setError(null);

      await userService.updateTShirtSize(size);

      // Update localStorage with the new t-shirt size
      try {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        storedUser.profile = storedUser.profile || {};
        storedUser.profile.tShirtSize = size;
        localStorage.setItem('user', JSON.stringify(storedUser));
      } catch (e) {
        console.error('Failed to update localStorage:', e);
      }

      // Set the dedicated flag to prevent showing dialog again
      localStorage.setItem(TSHIRT_SIZE_KEY, 'true');

      // Refresh user data to sync with backend
      if (refreshUserData) {
        try {
          await refreshUserData();
        } catch (e) {
          console.error('Failed to refresh user data:', e);
        }
      }

      setShowDialog(false);
    } catch (err) {
      setError(err.message || 'Failed to save t-shirt size. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [refreshUserData]);

  return (
    <>
      {children}
      <TShirtSizeDialog
        open={showDialog}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
      />
    </>
  );
};

export default TShirtSizePrompt;
